import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Dialog, Spinner } from "@/components/ui";
import { useNewCategories } from "@/utils/useNewCategories";
import { VideoScriptsServices } from "@/services/bedrock";
import { X, Plus } from "lucide-react";

interface CategoryExclusionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const CategoryExclusionModal: React.FC<CategoryExclusionModalProps> = ({ isOpen, onClose, onSaved }) => {
  const { categories } = useNewCategories("VIDEO_SCRIPT", true);

  const [excludedIds, setExcludedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      fetchExclusions().catch(console.error);
    }
  }, [isOpen, categories]);

  const fetchExclusions = async () => {
    setIsLoading(true);
    try {
      const ids = await VideoScriptsServices.getExcludedCategoryIds();
      setExcludedIds(ids);
    } catch (error) {
      console.error("Failed to load category exclusions", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await VideoScriptsServices.setExcludedCategoryIds(excludedIds);
      onSaved();
    } catch (error) {
      console.error("Failed to save category exclusions", error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = (id: number, isCurrentlyExcluded: boolean) => {
    if (isCurrentlyExcluded) {
      setExcludedIds(prev => prev.filter(excludedId => excludedId !== id));
    } else {
      setExcludedIds(prev => [...prev, id]);
    }
  };

  const hiddenCategories = useMemo(() => categories.filter(c => excludedIds.includes(c.id)), [categories, excludedIds]);

  const visibleCategories = useMemo(
    () => categories.filter(c => !excludedIds.includes(c.id)),
    [categories, excludedIds]
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={o => {
        if (!o) onClose();
      }}
      size={"xl"}
    >
      <Dialog.Header className="text-lg font-semibold text-gray-800">Customize Library Visibility</Dialog.Header>
      <Dialog.Description className="text-sm text-gray-600 px-4">
        Choose any categories you'd like to exclude. We'll hide all existing scripts in these categories, plus any new
        ones our content team adds in the future.
      </Dialog.Description>
      <Dialog.Content>
        {isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* Visible Categories Box */}
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-sm">Visible Categories</label>
              <div className="min-h-24 p-3 border border-gray-200 rounded-md bg-white flex flex-wrap gap-2 content-start shadow-inner">
                {visibleCategories.length === 0 ? (
                  <span className="text-gray-400 text-sm italic w-full text-center mt-2">
                    All global categories are hidden.
                  </span>
                ) : (
                  visibleCategories.map(category => (
                    <Badge
                      key={`visible-${category.id}`}
                      onClick={() => toggleCategory(category.id, false)}
                      className="cursor-pointer flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium hover:bg-blue-100 hover:border-blue-300 transition-colors"
                      title="Click to hide"
                    >
                      {category.name}
                      <X size={14} className="text-blue-500 hover:text-blue-700" />
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* Hidden Categories Box */}
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-sm">Hidden Categories</label>
              <div className="min-h-24 p-3 border border-gray-200 rounded-md bg-gray-50 flex flex-wrap gap-2 content-start shadow-inner">
                {hiddenCategories.length === 0 ? (
                  <span className="text-gray-400 text-sm italic w-full text-center mt-2">
                    No categories are currently hidden.
                  </span>
                ) : (
                  hiddenCategories.map(category => (
                    <Badge
                      key={`hidden-${category.id}`}
                      onClick={() => toggleCategory(category.id, true)}
                      className="cursor-pointer flex items-center gap-1.5 bg-gray-200 text-gray-600 border border-gray-300 rounded-full text-xs font-medium hover:bg-gray-300 transition-colors opacity-80 hover:opacity-100"
                      title="Click to show"
                    >
                      {category.name}
                      <Plus size={14} className="text-gray-500" />
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog.Content>
      <Dialog.Footer className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isLoading || isSaving} loading={isSaving}>
          {isSaving ? "Saving..." : "Save Preferences"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
